import {
  FEATURES,
  IGroupInfo,
  IMessage,
  ISuggestedClinicianData,
  ReferralRevokeFailException,
} from "@home-health-notify/domain";
import ChatIcon from "@mui/icons-material/Chat";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import {
  ApiErrorHandler,
  getReferralMessage,
  OrganizationContext,
  UserDirectoryContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useState } from "react";
import { ConfirmationDialog } from "../../../../common/ConfirmationDialog";
import { ClinicianDetails } from "./ClinicianDetails";
import { SendReferral } from "./SendReferral";
import {
  AddMemberActionButtons,
  CustomTooltipContent,
  ReferralMessageDetails,
  ReferralSendOptions,
  ReferralStatusText,
  SendReferralButton,
  SmartContentItemWrapper,
  TooltipBody,
  TooltipHeading,
} from "./SmartContent.style";

export const SmartContentItem = ({
  clinicianData,
  groupInfo,
  isMemberInGroup,
  isReferralMatch,
  message,
}: {
  clinicianData: ISuggestedClinicianData;
  groupInfo: IGroupInfo;
  isMemberInGroup: boolean;
  isReferralMatch: boolean;
  message: IMessage;
}) => {
  const { t } = useTranslation();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const tNs = (key: string) => t(key, { ns: "message" });
  const { enqueueSnackbar } = useSnackbar();
  const clinicianId = clinicianData.id;
  const { userDirectory } = useContext(UserDirectoryContext);
  const user = userDirectory.getUserInfo(clinicianId);

  const pendingReferrals = groupInfo.pendingReferrals ?? [];
  const referralMessage = getReferralMessage(pendingReferrals, clinicianId);
  const isSendReferralsAllowed =
    selectedOrgUser?.hasAccess(FEATURES.SEND_REFERRALS) ?? false;
  const [isSendReferralModalOpen, setIsSendReferralModalOpen] =
    useState<boolean>(false);
  const handleSendReferralModalOpen = () => {
    setIsSendReferralModalOpen(true);
  };
  const handleSendReferralModalClose = () => {
    setIsSendReferralModalOpen(false);
  };

  const [
    isRevokeRefConfirmationDialogOpen,
    setRevokeRefConfirmationDialogOpen,
  ] = useState(false);
  const onOpenRevokeRefConfirmationDialog = () => {
    setRevokeRefConfirmationDialogOpen(true);
  };
  const onCloseRevokeRefConfirmationDialog = () => {
    setRevokeRefConfirmationDialogOpen(false);
  };
  const [
    isAddMemberConfirmationDialogOpen,
    setAddMemberConfirmationDialogOpen,
  ] = useState(false);
  const onOpenAddMemberConfirmationDialog = () => {
    setAddMemberConfirmationDialogOpen(true);
  };
  const onCloseAddMemberConfirmationDialog = () => {
    setAddMemberConfirmationDialogOpen(false);
  };

  const onAddMember = async (selectedId: string) => {
    const chatId: string = groupInfo.id;
    const memberToAdd: any = {
      membersToAdd: [
        {
          userId: selectedId,
        },
      ],
    };

    try {
      if (selectedOrgUser) {
        await selectedOrgUser.chatManager
          .getPatientChatManager()
          .addMembers(memberToAdd, chatId);

        enqueueSnackbar(groupInfo.name + tNs("membersAddedSuccessfully"), {
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      ApiErrorHandler(error, enqueueSnackbar);
    }
  };

  const onRevokeReferral = async (clincianId: string) => {
    try {
      await message.revokeReferral(clincianId, groupInfo.id);
      enqueueSnackbar(
        tNs("referral.invitationFor") +
          user.name +
          tNs("referral.hasBeenRevoked"),
        {
          autoHideDuration: 3000,
        },
      );
    } catch (e) {
      if (e instanceof ReferralRevokeFailException) {
        enqueueSnackbar(tNs("referral.failedRevokingReferral"), {
          autoHideDuration: 3000,
        });
      } else {
        ApiErrorHandler(e, enqueueSnackbar);
      }
    }
  };

  return (
    <Box>
      <SmartContentItemWrapper data-testid="smart-content-item-wrapper">
        <ClinicianDetails clinicianData={clinicianData} />
        {isMemberInGroup ? (
          <ReferralStatusText>
            {tNs("smartContent.addedToGroup")}
          </ReferralStatusText>
        ) : isReferralMatch ? (
          <ReferralSendOptions>
            <ReferralMessageDetails>
              <ReferralStatusText>
                {tNs("smartContent.sentReferral")}
              </ReferralStatusText>
              <Tooltip
                title={
                  <CustomTooltipContent>
                    <TooltipHeading data-testid="referral-message-heading">
                      {tNs("smartContent.referralMessage")}
                    </TooltipHeading>
                    <TooltipBody data-testid="referral-message-body">
                      {referralMessage ? referralMessage : "N/A"}
                    </TooltipBody>
                  </CustomTooltipContent>
                }
                placement="top"
              >
                <IconButton>
                  <ChatIcon fontSize="small" data-testid="info-btn" />
                </IconButton>
              </Tooltip>
            </ReferralMessageDetails>
            {isSendReferralsAllowed && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                data-testid="referral-revoke-btn"
                onClick={onOpenRevokeRefConfirmationDialog}
              >
                {tNs("referral.revoke")}
              </Button>
            )}
          </ReferralSendOptions>
        ) : (
          <AddMemberActionButtons>
            {isSendReferralsAllowed && (
              <SendReferralButton
                variant="outlined"
                size="small"
                onClick={handleSendReferralModalOpen}
                data-testid="send-referral-btn"
              >
                {tNs("referral.sendReferral")}
              </SendReferralButton>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenAddMemberConfirmationDialog}
            >
              {tNs("referral.add")}
            </Button>
          </AddMemberActionButtons>
        )}
        <SendReferral
          isSendReferralModalOpen={isSendReferralModalOpen}
          closeSendReferralModal={handleSendReferralModalClose}
          clinicianData={clinicianData}
          groupInfo={groupInfo}
          userInfo={user}
        />
        <ConfirmationDialog
          isOpen={isRevokeRefConfirmationDialogOpen}
          onClose={onCloseRevokeRefConfirmationDialog}
          confirmationTitle={tNs("referral.revokeReferral")}
          confirmationMessage={tNs("referral.confirmRevokeReferral")}
          confirmationButtonText={tNs("referral.revoke")}
          onConfirmationAction={() => onRevokeReferral(clinicianData.id)}
        />
        <ConfirmationDialog
          isOpen={isAddMemberConfirmationDialogOpen}
          onClose={onCloseAddMemberConfirmationDialog}
          confirmationTitle={tNs("addUser")}
          confirmationMessage={
            tNs("confirmAddUserPrefix") +
            user.name +
            tNs("confirmAddUserSuffix")
          }
          confirmationButtonText={tNs("add")}
          onConfirmationAction={() => onAddMember(clinicianData.id)}
        />
      </SmartContentItemWrapper>
    </Box>
  );
};
